/**
 * Subscription store carrying the team-wide unread text-conversation count.
 *
 * The count deliberately does not live in React state that
 * `UnreadTextCountProvider`'s own JSX reads: the provider wraps `<Router />`, so
 * a provider-local `setState` would re-render the provider itself on every
 * inbound text. Holding the value here and handing consumers
 * `useSyncExternalStore` means a count tick reaches only the components that
 * read it, and is tearing-safe under concurrent rendering.
 *
 * `undefined` means "unavailable" and is the initial value. There is no `0`
 * default -- an absent count and a zero count both render no badge, but the
 * distinction must survive the plumbing so nothing downstream can coerce a
 * missing registry item into a real zero.
 */
export interface UnreadTextCountStore {
  getCount: () => number | undefined;
  /** Single writer: `UnreadTextCountHost`. */
  setCount: (count: number | undefined) => void;
  subscribe: (listener: () => void) => () => void;
}

export const createUnreadTextCountStore = (): UnreadTextCountStore => {
  let count: number | undefined;
  const listeners = new Set<() => void>();

  return {
    getCount: () => count,
    setCount: (next) => {
      if (next === count) {
        return;
      }
      count = next;
      listeners.forEach((listener) => listener());
    },
    subscribe: (listener) => {
      listeners.add(listener);

      return () => {
        listeners.delete(listener);
      };
    },
  };
};

/**
 * Read-only store used when no provider is present. The read path is lenient on
 * purpose: `MessagesNavBadge` lives inside `MainLayout`, which plenty of tests
 * and stories render on their own, and "no provider" should mean "no badge"
 * rather than a crash. The write path is strict instead -- see
 * `useUnreadTextCountSetter`.
 */
export const UNAVAILABLE_UNREAD_TEXT_COUNT_STORE: UnreadTextCountStore = {
  getCount: () => undefined,
  setCount: () => undefined,
  subscribe: () => () => undefined,
};
